import { Injectable, Inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { tap, map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { UserRightsService } from './user-rights.service';
import { ToastrService } from 'ngx-toastr';
import { Environment } from '@huge-monorepo/hip/services';
import { IGenericService, IHIPGenericService } from '@huge-monorepo/shared/interfaces';

@Injectable({
  providedIn: 'root',
})
export class GenericService implements IHIPGenericService {
  constructor(
    private http: HttpClient,
    private router: Router,
    private auth: UserRightsService,
    @Inject(DOCUMENT) private document: Document,
    @Inject('WINDOW') private window: any,
    private toastr: ToastrService,
    @Inject(Environment) private environment: any,
  ) { }

  private createHeaders(authRequired: boolean = true, otp: string = '', custom: Object[] = [], contentType: string = 'application/json'): HttpHeaders {
    let ob = new HttpHeaders();
    if (contentType != '') {
      ob = ob.append('Content-Type', contentType);
    }
    ob = ob.append('X-Huge-Otp', otp);
    ob = ob.append('X-AREA', 'HIP');

    if (custom != null) {
      custom.forEach((val) => {
        if (val['Text'] != null && val['Value'] != null) {
          ob = ob.append(val['Text'], val['Value']);
        }
      });
    }

    if (authRequired === true) {
      // Use the main auth service to get the token
      let token = this.auth.GetToken();
      if (token != null) {
        ob = ob.append('Authorization', 'bearer ' + token);
      }
    }

    return ob;
  }

  public CreateFormData(files: FileList, jsonString: string): FormData {
    let data = new FormData();
    if (files != null) {
      for (let i = 0; i < files.length; i++) {
        data.append('documents', files.item(i));
      }
    }
    data.append('body', jsonString); // JSON string
    return data;
  }

  public UploadFiles(urlPart: string, files: FileList, objToSend: Object, requiresAuth: boolean = true) {
    let fd = this.CreateFormData(files, JSON.stringify(objToSend));
    return this.POST(urlPart, fd, requiresAuth, null, '');
    // "multipart/form-data"
  }

  public createToast(header: string, description: string, type: string = 'success', timeout: number = 3) {
    this.toastr[type](description, header, {
      extendedTimeOut: timeout * 1000,
      progressBar: true,
    });
  }

  public DELETE(urlPart: string, requiresAuth: boolean = true): Observable<Object> {
    // console.trace();
    // console.log('urlPart',JSON.stringify(urlPart))
    // let url:string = this.getBase() + urlPart;
    // console.log("url", url)
    return this.http
      .delete(urlPart, {
        headers: this.createHeaders(requiresAuth, '', [], 'application/json'),
        responseType: 'json',
      })
      .pipe(
        tap(
          (data) => () => { },
          (error) => {
            this.auth.getError(error, this.router)
          },
        ),
        catchError((err) => {
          throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
        }),
      );
  }

  public PUT(urlPart: string, objToSend: Object, requiresAuth: boolean = true): Observable<Object> {
    // console.log("Im putting data: ", urlPart, requiresAuth, (new Date()).toUTCString());
    // console.log('urlPart',JSON.stringify(urlPart))
    // console.trace();
    return this.http
      .put(urlPart, objToSend, {
        headers: this.createHeaders(requiresAuth, '', [], 'application/json'),
        responseType: 'json',
      })
      .pipe(
        tap(
          (data) => () => {
            // console.log("put data success: ", (new Date()).toUTCString());
          },
          (error) => () => {
            // console.log("put data error: ", error, this.router, (new Date()).toUTCString());
            this.auth.getError(error, this.router)
          },
        ),
        catchError((err) => {
          // console.log("put data success: ", (new Date()).toUTCString());
          if (err instanceof HttpErrorResponse) {
            if (err.error && err.error.message) {
              if (err.error.message === "Undocumented Claims") {
                throw JSON.stringify(err);
              }
            }
          }


          throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
        }),
      );
  }

  public GET(urlPart: string, requiresAuth: boolean = true, response: any = 'json', otp: string = ''): Observable<Object> {
    // console.log("Im getting data: ", urlPart, requiresAuth, (new Date()).toUTCString());
    // console.log('urlPart',JSON.stringify(urlPart))
    // console.trace();
    // console.log(urlPart)
    return this.http.get(urlPart, {
      headers: this.createHeaders(requiresAuth, otp, [], 'application/json'),
      responseType: response,
      observe: 'response',
    });
  }

  public POST(urlPart: string, objectToSend: Object, requiresAuth: boolean = true, customHeaders: Object[] = [], contentType: string = 'application/json'): Observable<Object> {
    // Validate token before making request
    // console.log('urlPart',JSON.stringify(urlPart))
    // console.trace();

    return this.http
      .post(urlPart, objectToSend, {
        headers: this.createHeaders(requiresAuth, '', customHeaders, contentType),
        responseType: 'json',
      })
      .pipe(
        tap(
          (data) => () => { },
          (error) => {
            this.auth.getError(error, this.router)
          },
        ),
        catchError((err) => {
          throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
        }),
      );
  }

  public POSTANDOBSERVE(urlPart: string, objectToSend: Object, requiresAuth: boolean = true): Observable<Object> {
    // console.log('urlPart',JSON.stringify(urlPart))
    // console.trace();
    return this.http
      .post(urlPart, objectToSend, {
        headers: this.createHeaders(requiresAuth, '', [], 'application/json'),
        responseType: 'json',
        observe: 'response',
      })
      .pipe(
        tap(
          (data) => () => { },
          (error) => {
            this.auth.getError(error, this.router)
          },
        ),
        catchError((err) => {
          throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
        }),
      );
  }

  // public getFullList(urlPart: string, oui: string, productClass: string, fileTypeId: string, count: number,
  //                    offset: number, searchTerm: string, orderBy: string, desc: Boolean): Observable<Object> {
  //   let url: string = this.getBase() +
  //     urlPart +
  //     'count=' + count +
  //     '&offset=' + offset +
  //     '&fileTypeId=' + fileTypeId;
  //   if (oui !== '') {
  //     url += '&ouid=' + oui;
  //   }
  //   if (productClass !== '') {
  //     url += '&productClass=' + productClass;
  //   }
  //   if (searchTerm !== '') {
  //     url += '&search=' + searchTerm;
  //   }
  //   if (orderBy !== '') {
  //     url += '&orderBy=' + orderBy;
  //     if (desc != null) {
  //       url += '&desc=' + (desc === true ? 'true' : 'false');
  //     }
  //   }
  //   // console.log(url);
  //   return this.http.get(url, { headers: new HttpHeaders({
  //       'Content-Type': 'application/json',
  //       Authorization: 'bearer ' + this.auth.GetToken(),
  //     }), responseType: 'json'}).pipe (
  //       tap (
  //         data => () => { },
  //         error => this.auth.getError(error, this.router)
  //       ),
  //       catchError((err) => {
  //         throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
  //       }));
  // }

  // public getFullListHistory(urlPart: string, fileTypeId: string, count: number, offset: number, searchTerm: string, limit: string,
  //                           orderBy: string, desc: Boolean): Observable<Object> {
  //   let url: string = this.getBase() +
  //     urlPart +
  //     'count=' + count +
  //     '&offset=' + offset +
  //     '&limit=' + limit +
  //     '&fileTypeId=' + fileTypeId;
  //   if (searchTerm !== '') {
  //     url += '&search=' + searchTerm;
  //   }
  //   if (orderBy !== '') {
  //     url += '&orderBy=' + orderBy;
  //     if (desc != null) {
  //       url += '&desc=' + (desc === true ? 'true' : 'false');
  //     }
  //   }
  //   // console.log(url);
  //   return this.http.get(url, { headers: new HttpHeaders({
  //       'Content-Type': 'application/json',
  //       Authorization: 'bearer ' + this.auth.GetToken(),
  //     }), responseType: 'json'}).pipe (
  //       tap (
  //         data => () => { },
  //         error => this.auth.getError(error, this.router)
  //       ),
  //       catchError((err) => {
  //         throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
  //       }));
  // }

  // public getFullListCount(urlPart: string, searchTerm: string, orderBy: string, desc: Boolean): Observable<Object> {
  //   let url: string = this.getBase() + urlPart + '?search=' + searchTerm;
  //   if (orderBy != '') {
  //     url += '&orderBy=' + orderBy;
  //     if (desc != null) {
  //       url += '&desc=' + (desc === true ? 'true' : 'false');
  //     }
  //   }
  //   return this.http.get(url, { headers: new HttpHeaders({
  //       'Content-Type': 'application/json',
  //       Authorization: 'bearer ' +  this.auth.GetToken(),
  //   }), responseType: 'json'}).pipe (
  //     tap (
  //       data => () => { },
  //       error => this.auth.getError(error, this.router)
  //     ),
  //     catchError((err) => {
  //       throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
  //     }));
  // }

  // public getList(urlPart:string, count:number, offset:number, searchTerm:string): Observable<Object> {//'/customers/?
  //   if (searchTerm == null) {
  //     searchTerm = "";
  //   }
  //   let url:string = this.getBase()+urlPart+'count='+count+'&offset='+offset+'&search='+searchTerm + '&opn';
  //   return this.http.get(url, { headers: new HttpHeaders({
  //     'Content-Type': 'application/json',
  //     Authorization: 'bearer ' + this.auth.GetToken(),
  //   }), responseType: 'json'}).pipe (
  //     tap (
  //       data => () => { },
  //       error => this.auth.getError(error, this.router)
  //     ),
  //     catchError((err) => {
  //       throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
  //     }));
  // }

  // public getListWObj(urlPart: string, obj: {}): Observable<Object> {

  //   return this.http.post(urlPart, obj, { headers: new HttpHeaders({
  //     'Content-Type': 'application/json',
  //     Authorization: 'bearer ' + this.auth.GetToken(),
  //   }), responseType: 'json'}).pipe (
  //     tap (
  //       data => () => { },
  //       error => this.auth.getError(error, this.router)
  //     ),
  //     catchError((err) => {
  //       throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
  //     }));
  // }

  public validateAllowedEmails(email: string, validateDomain: boolean = true): boolean {
    let regex;
    if (email == 'rynhardtcloete@gmail.com') {
      // TODO: Remove when testing is completed.
      return true;
    }
    if (email == 'damian@monkeyandriver.com') {
      // TODO: Remove when testing is completed.
      return true;
    }
    if (email == 'obakeng@monkeyandriver.com') {
      // TODO: Remove when testing is completed.
      return true;
    }
    if (validateDomain) {
      regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@hugeconnect.co.za$/;
    } else {
      regex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    }
    return regex.test(email);
  }

  // public postListCountWObjectAndCount(urlPart: string, count: number, offset: number,
  //                                     searchTerm: string, obj: object): Observable<Object> {
  //     const url: string = this.getBase() + urlPart; // + 'count='+count+'&offset='+offset+'&search='+searchTerm;
  //     // console.log("URL",url);
  //     return this.http.post(url, obj, { headers: new HttpHeaders({
  //       'Content-Type': 'application/json',
  //       Authorization: 'bearer ' + this.auth.GetToken(),
  //     }), responseType: 'json'}).pipe (
  //       tap (
  //         data => () => { },
  //         error => this.auth.getError(error, this.router)
  //       ),
  //       catchError((err) => {
  //         throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
  //       }));
  //   }

  // public getFileUniqueness(urlPart: string, fileName: string): Observable<Object> {
  //   let url: string = this.getBase() + urlPart;
  //   if (fileName != '') {
  //     url += '?fileName=' + fileName;
  //   }
  //   return this.http.get(url, { headers: new HttpHeaders({
  //       'Content-Type': 'application/json',
  //       Authorization: 'bearer ' +  this.auth.GetToken(),
  //     }), responseType: 'json'}).pipe (
  //       tap (
  //         data => () => { },
  //         error => this.auth.getError(error, this.router)
  //       ),
  //       catchError((err) => {
  //         throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
  //       }));
  // }

  public getListCount_t(urlPart: string, fileId: string, searchTerm: string): Observable<Object> {
    let url: string = this.getBase() + urlPart;

    url += '?fileTypeId=' + fileId;

    if (searchTerm != '') {
      url += '&search=' + searchTerm;
    }
    return this.http
      .get(url, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': 'bearer ' + this.auth.GetToken(),
        }),
        responseType: 'json',
      })
      .pipe(
        tap(
          (data) => () => { },
          (error) => this.auth.getError(error, this.router),
        ),
        catchError((err) => {
          throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
        }),
      );
  }

  // public getListCount(urlPart: string, searchTerm: string): Observable<Object> {
  //   let url: string = this.getBase() + urlPart;
  //   if (searchTerm !='') {
  //       url += '?search=' + searchTerm;
  //   }
  //   return this.http.get(url, { headers: new HttpHeaders({
  //       'Content-Type': 'application/json',
  //       Authorization: 'bearer ' +  this.auth.GetToken(),
  //   }), responseType: 'json'}).pipe (
  //     tap (
  //       data => () => { },
  //       error => this.auth.getError(error, this.router)
  //     ),
  //     catchError((err) => {
  //       throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
  //     }));
  // }

  // public getListCountWObj(urlPart: string, ob: {}): Observable<Object> {
  //   const url: string = this.getBase() + urlPart;
  //   return this.http.post(url, ob, { headers: new HttpHeaders({
  //     'Content-Type': 'application/json',
  //     Authorization: 'bearer ' + this.auth.GetToken()
  //   }), responseType: 'json'}).pipe (
  //     tap (
  //       data => () => { },
  //       error => this.auth.getError(error, this.router)
  //     ),
  //     catchError((err) => {
  //       throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
  //     }));
  // }

  getBase(): string {
    return this.environment.apiLink;
  }

  public getFilterTypes(): Observable<Object> {
    return of([
      { iD: 1, name: 'Customer' },
      { iD: 2, name: 'Device Type' },
      { iD: 3, name: 'Device' },
      { iD: 4, name: 'Sim' },
    ]);
  }

  downloadFile(urlPart: string, objectToSend: any, method: string = 'post'): Observable<any> {
    // let headers = new HttpHeaders({
    //   'Content-Type': 'application/json',
    //   'Authorization': 'bearer ' + this.auth.GetToken(),
    // });
    let headers = this.createHeaders(true, '', [], 'application/json');

    switch (method) {
      case 'get':
        return this.http
          .get(urlPart, {
            headers: headers,
            observe: 'response',
            responseType: 'blob',
          })
          .pipe(
            tap(
              (data) => () => { },
              // (error) => this.auth.getError(error, this.router),
              (error) => {
                this.auth.getError(error, this.router);
              }
            ),
            catchError((err) => {
              throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
            }),
          );
      default:
        return this.http
          .post(urlPart, objectToSend, {
            headers: headers,
            observe: 'response',
            responseType: 'blob',
          })
          .pipe(
            tap(
              (data) => () => { },
              // (error) => this.auth.getError(error, this.router),
              (error) => {
                this.auth.getError(error, this.router);
              }
            ),
            catchError((err) => {
              throw JSON.stringify(err); // Use console.log(err) for detail
            }),
          );
    }
  }

  public download(urlPart: string, obj: Object | null, method: string = 'post', toDownload: Boolean = true, fileName?: string, imageActions: Boolean = false): Observable<any> {
    // console.log({urlPart})
    return this.downloadFile(urlPart, obj, method).pipe(
      map((data) => {
        if (toDownload == true) {
          var blob = new Blob([data.body], { type: data.headers.get('content-type') });
          let disposition = data.headers.get('Content-Disposition');
          let fn = fileName;
          if (disposition && disposition.length >= 21) {
            fn = disposition.substring(21, disposition.length);
          }

          if (!fn || fn.length == 0) {
            fn = '';
            let m = data.headers.get('content-type').split('/');
            if (m && m.length > 1) {
              fn = '.' + m[1];
            }
            fn = 'example' + fn;
          }

          const linkURL = this.window.URL.createObjectURL(blob);
          const nav = window.navigator as any;
          if (nav.msSaveOrOpenBlob) {
            //IE & Edge
            //msSaveBlob only available for IE & Edge
            (window.navigator as any).msSaveBlob(data, fn);
          } //Chrome & FF
          else {
            const anchor = this.document.createElement('a');
            anchor.href = linkURL;
            anchor.download = fn;
            this.document.body.appendChild(anchor); //For FF
            anchor.click();
            //It's better to remove the elem
            this.document.body.removeChild(anchor);
          }
        } else {
          var blob = new Blob([data.body], { type: data.body.type });
          let disposition = data.headers.get('Content-Disposition');
          let fileName = disposition.substring(21, disposition.length);
          const linkURL = this.window.URL.createObjectURL(blob);

          if (imageActions) {
            const newWindow = window.open(linkURL, fileName);
            if (!newWindow) return of(false);

            newWindow.document.write(`
<html>
  <head>
    <title>Image Preview</title>
    <style>
      body {
        margin: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        background: #111;
        color: white;
        height: 100vh;
        font-family: Arial, sans-serif;
        overflow: hidden;
      }

      .viewer {
        flex: 1;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;
        background: #000;
      }

      img {
        max-width: 100%;
        max-height: 100%;
        transition: transform 0.1s ease;
        border-radius: 10px;
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
        transform-origin: center center;
        user-select: none;
        cursor: grab;
      }

      img:active {
        cursor: grabbing;
      }

      .controls {
        padding: 10px;
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        justify-content: center;
        background: #222;
        width: 100%;
        border-top: 1px solid #333;
      }

      button {
        background: #333;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        transition: background 0.2s, transform 0.1s;
      }
      button:hover { background: #555; }
      button:active { transform: scale(0.95); }
    </style>
  </head>
  <body>
    <div class="viewer">
      <img id="preview-img" src="${linkURL}" />
    </div>
    <div class="controls">
      <button id="rotate-left">⟲ Rotate Left</button>
      <button id="rotate-right">⟳ Rotate Right</button>
      <button id="reset-rotation">Reset Rotation</button>
      <button id="zoom-in">＋ Zoom In</button>
      <button id="zoom-out">－ Zoom Out</button>
      <button id="reset-zoom">Reset Zoom</button>
    </div>
    <script>
      const img = document.getElementById('preview-img');
      const viewer = document.querySelector('.viewer');
      let rotation = 0, scale = 1;
      let translateX = 0, translateY = 0;
      let dragging = false, startX = 0, startY = 0;

      // Bound functions
      const onMouseMove = (e) => {
        if (!dragging) return;
        e.preventDefault();
        translateX = e.clientX - startX;
        translateY = e.clientY - startY;
        applyTransform();
      };

      const onMouseUp = () => {
        dragging = false;
        img.style.cursor = 'grab';
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };

      function getBounds() {
        const viewerRect = viewer.getBoundingClientRect();
        const rect = img.getBoundingClientRect();
        const rotated = rotation % 180 !== 0;
        const width = rotated ? rect.height : rect.width;
        const height = rotated ? rect.width : rect.height;
        return {
          maxX: Math.max((width - viewerRect.width) / 2, 0),
          maxY: Math.max((height - viewerRect.height) / 2, 0)
        };
      }

      function clampTranslation() {
        const bounds = getBounds();
        translateX = Math.min(Math.max(translateX, -bounds.maxX), bounds.maxX);
        translateY = Math.min(Math.max(translateY, -bounds.maxY), bounds.maxY);
      }

      function applyTransform() {
        clampTranslation();
        img.style.transform = 'translate(' + translateX + 'px,' + translateY + 'px) rotate(' + rotation + 'deg) scale(' + scale + ')';
      }

      document.getElementById('rotate-left').onclick = () => {
        rotation -= 90;
        applyTransform();
      };

      document.getElementById('rotate-right').onclick = () => {
        rotation += 90;
        applyTransform();
      };

      document.getElementById('reset-rotation').onclick = () => {
        rotation = 0;
        applyTransform();
      };

      document.getElementById('zoom-in').onclick = () => {
        scale += 0.1;
        applyTransform();
      };

      document.getElementById('zoom-out').onclick = () => {
        scale = Math.max(0.1, scale - 0.1);
        applyTransform();
      };

      document.getElementById('reset-zoom').onclick = () => {
        scale = 1;
        translateX = 0; 
        translateY = 0;
        applyTransform();
      };

      // Mouse drag for panning
      img.addEventListener('mousedown', e => {
        if (scale <= 1) return;
        e.preventDefault();
        dragging = true;
        startX = e.clientX - translateX;
        startY = e.clientY - translateY;
        img.style.cursor = 'grabbing';

        viewer.addEventListener('mousemove', onMouseMove, { passive: false });
        window.addEventListener('mouseup', onMouseUp);
      });

      // Mouse wheel zoom
      img.addEventListener('wheel', function(e) {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault(); // Only prevent default when zooming
          if (e.deltaY < 0) scale += 0.1;
          else scale = Math.max(0.1, scale - 0.1);
          applyTransform();
        }
      });
    </script>
  </body>
</html>
`);
newWindow.document.close();

          } else {
            this.window.open(linkURL, fileName);
          }
        }

        return of(true);
      }),
    );
  }

  getAlertList(urlPart: string, count: number, offset: number, searchTerm: string, oldFiles: boolean): Observable<Object> {
    let url: string = this.getBase() + urlPart + 'count=' + count + '&offset=' + offset + '&search=' + searchTerm + '&oldFileRule=' + (oldFiles === true ? 'true' : 'false');
    return this.http
      .get(url, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': 'bearer ' + this.auth.GetToken(),
        }),
        responseType: 'json',
      })
      .pipe(
        tap(
          (data) => () => { },
          (error) => this.auth.getError(error, this.router),
        ),
        catchError((err) => {
          throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
        }),
      );
  }

  // getAdvancedAlerts(urlPart:string, count:number, offset:number, searchTerm:string, advancedSearchTerm:object, oldFiles: boolean, orderBy: string, desc: Boolean): Observable<Object> {
  //   let url: string = this.getBase() +
  //                     urlPart +
  //                     'count=' + count +
  //                     '&offset=' + offset +
  //                     '&search=' + searchTerm +
  //                     '&oldFileRule=' + (oldFiles === true ? 'true' : 'false') +
  //                     '&orderBy=' + orderBy +
  //                     '&desc=' + (desc === true ? 'true' : 'false');
  //                     // console.log(url);
  //   return this.http.post(url, advancedSearchTerm, { headers: new HttpHeaders({
  //     'Content-Type': 'application/json',
  //     'Authorization': 'bearer ' + this.auth.GetToken(),
  //   }), responseType: 'json'}).pipe (
  //     tap (
  //       data => () => { },
  //       error => this.auth.getError(error, this.router)
  //     ),
  //     catchError((err) => {
  //       throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
  //     }));
  // }

  // getAdvancedAlertCount(urlPart: string, searchTerm: string, obj: Object): Observable<Object> {
  //   let url:string = this.getBase()+urlPart;
  //   if(searchTerm!=""){
  //     url += '?search='+searchTerm;
  //   }
  //   return this.http.post(url, obj, { headers: new HttpHeaders({
  //     'Content-Type': 'application/json',
  //     'Authorization': 'bearer '+  this.auth.GetToken(),
  //   }), responseType: 'json'}).pipe (
  //     tap (
  //       data => () => { },
  //       error => this.auth.getError(error, this.router)
  //     ),
  //     catchError((err) => {
  //       throw 'Error in source. Details: ' + err; // Use console.log(err) for detail
  //     }));
  // }
}
